/**
 * Representative FIRST-LOAD skeletons for the dashboard cards (owner ruling
 * after browser review, 2026-07-15: keep a loading indicator but make it
 * representative -- the old page-level "Loading dashboard..." text gate is
 * gone; the page header + period controls render immediately and each card
 * pops in independently when its own data lands). Shapes deliberately track
 * each card's real rendered dimensions (same Paper padding, row rhythm and
 * bar heights) so the swap-in causes no visible jump. These render INSIDE
 * the card components behind a `loading` prop, so DashboardPage's
 * permission gates (revenue:read / shops:read) automatically gate the
 * skeletons too -- an External session sees NO placeholder for a card it
 * will never get (zero trace includes loading states). Respects
 * prefers-reduced-motion via animation={false} (Skeleton pulses by
 * default).
 */
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';

import { prefersReducedMotion } from './chartHelpers.ts';

/** Shared "pulse unless reduced motion" knob -- undefined keeps MUI's
 *  default pulse animation. */
function skeletonAnimation(): false | undefined {
  return prefersReducedMotion() ? false : undefined;
}

/** Left-label / right-amount widths per statement row, mimicking Gross
 *  sales / less Payout / = Gross profit / less Tax / = Net profit; `chip`
 *  marks the rows whose real counterpart carries a delta chip -- every row
 *  EXCEPT Tax (StatementCard: "Tax gets no chip"). */
const STATEMENT_ROWS: ReadonlyArray<{ label: number; amount: number; chip?: boolean }> = [
  { label: 96, amount: 128, chip: true },
  { label: 84, amount: 100, chip: true },
  { label: 108, amount: 108, chip: true },
  { label: 64, amount: 92 },
  { label: 100, amount: 120, chip: true },
];

/** Exported for StatementCard.test.tsx's parity test, which mechanically
 *  couples these skeleton shapes to the real card: the skeleton's row-bar
 *  count must equal the real statement row count and its chip-pill count
 *  the real DeltaChip count, so a future row/chip change fails a test
 *  instead of silently drifting apart. */
export const STATEMENT_SKELETON_ROW_COUNT = STATEMENT_ROWS.length;
export const STATEMENT_SKELETON_CHIP_COUNT = STATEMENT_ROWS.filter((r) => r.chip).length;

/** Financial-statement card ghost: title-width bar, 5 varying-width row
 *  pairs on the real rows' py rhythm, chip-shaped blocks where the delta
 *  chips sit. */
export function StatementCardSkeleton(): React.JSX.Element {
  const animation = skeletonAnimation();
  return (
    <Paper data-testid="statement-skeleton" sx={{ p: 2.5, width: '100%', height: { xs: 'auto', md: '100%' }, boxSizing: 'border-box' }}>
      <Skeleton animation={animation} width={150} height={28} sx={{ mb: 0.5 }} />
      {STATEMENT_ROWS.map((row) => (
        <Box key={`${row.label}-${row.amount}`} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', py: 1.25 }}>
          <Box>
            <Skeleton animation={animation} width={row.label} />
            {row.chip && <Skeleton animation={animation} variant="rounded" width={90} height={22} sx={{ mt: 0.75 }} />}
          </Box>
          <Skeleton animation={animation} width={row.amount} height={26} />
        </Box>
      ))}
    </Paper>
  );
}

/** Shared ghost for the small headline cards (Government take / 1NL
 *  revenue): caption-width bar, fat headline bar, a few label/amount row
 *  pairs. `padding` matches the real card's Paper padding (gov take 2.5,
 *  revenue 2) so nothing shifts on swap-in. */
export function HeadlineCardSkeleton({
  testId,
  rows,
  padding,
}: Readonly<{ testId: string; rows: number; padding: number }>): React.JSX.Element {
  const animation = skeletonAnimation();
  return (
    <Paper data-testid={testId} sx={{ p: padding, width: '100%', display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <Skeleton animation={animation} width={110} height={14} />
      <Skeleton animation={animation} width={140} height={30} />
      {Array.from({ length: rows }, (_, i) => (
        <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
          <Skeleton animation={animation} width={120} height={16} />
          <Skeleton animation={animation} width={70} height={16} />
        </Box>
      ))}
    </Paper>
  );
}

const GHOST_ROW_COUNT = 5;

/** Ghost body rows for the ranked Shops table -- the REAL column headers
 *  stay (ShopsTable renders them regardless), these fill the body with
 *  text-bar cells matching the live column layout, including the compact
 *  and revenue-column variants. */
export function ShopsTableGhostRows({
  compact,
  revenueColumnVisible,
  expanderColumnVisible = false,
}: Readonly<{ compact: boolean; revenueColumnVisible: boolean; expanderColumnVisible?: boolean }>): React.JSX.Element {
  const animation = skeletonAnimation();
  return (
    <>
      {Array.from({ length: GHOST_ROW_COUNT }, (_, i) => (
        <TableRow key={i} data-testid="shops-ghost-row">
          {/* Blank, like the header's expander cell -- keeps the ghost row's
              column count matching the real header. */}
          {expanderColumnVisible && <TableCell />}
          {!compact && (
            <TableCell>
              <Skeleton animation={animation} width={16} />
            </TableCell>
          )}
          <TableCell>
            <Skeleton animation={animation} width={160} />
          </TableCell>
          <TableCell align="right">
            <Skeleton animation={animation} width={72} sx={{ ml: 'auto' }} />
          </TableCell>
          {!compact && (
            <TableCell align="right">
              <Skeleton animation={animation} width={72} sx={{ ml: 'auto' }} />
            </TableCell>
          )}
          <TableCell align="right">
            <Skeleton animation={animation} width={48} sx={{ ml: 'auto' }} />
          </TableCell>
          {!compact && (
            <TableCell align="right">
              <Skeleton animation={animation} width={72} sx={{ ml: 'auto' }} />
            </TableCell>
          )}
          {revenueColumnVisible && (
            <TableCell align="right">
              <Skeleton animation={animation} width={72} sx={{ ml: 'auto' }} />
            </TableCell>
          )}
          <TableCell>
            <Skeleton animation={animation} variant="rounded" width={64} height={20} />
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}
